import { useEffect } from "react";
import { mutate } from "swr";
import { storage } from "@unifiedpatents/sso";

// How long to wait after the network returns for SSO to renew an ID token that expired while
// offline, and how often to look
const WAIT_MS = 10_000;
const POLL_MS = 250;

// Whether a request sent now carries a usable token: none at all (signed out), or an unexpired one
const tokenUsable = () => {
  const token = storage.getItem("idToken");
  if (!token) return true;
  try {
    const { exp } = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return exp * 1000 > Date.now();
  } catch (e) {
    return false;
  }
};

/**
 * Refetches what is on screen when the network comes back, as SWR's revalidateOnReconnect would,
 * but only once the stored ID token is usable, or after WAIT_MS. A laptop waking from sleep
 * reconnects before SSO has renewed a token that expired meanwhile, and the API would answer the
 * expired one as if the user were signed out. The SWRConfig in app.js turns SWR's own reconnect
 * refetch off. Unlike SWR's, this refetches every mounted query, including one that opted out of
 * reconnect refetching (member landing's number lookups, which never go stale).
 */
export default function useRevalidateOnReconnect() {
  useEffect(() => {
    let timer;
    const onOnline = () => {
      clearTimeout(timer);
      const giveUpAt = Date.now() + WAIT_MS;
      const check = () => {
        if (tokenUsable() || Date.now() >= giveUpAt) {
          mutate(() => true);
        } else {
          timer = setTimeout(check, POLL_MS);
        }
      };
      check();
    };
    window.addEventListener("online", onOnline);
    return () => {
      window.removeEventListener("online", onOnline);
      clearTimeout(timer);
    };
  }, []);
}
